import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerService } from '../../../services/customer.service';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TabsModule } from 'primeng/tabs';
import { InputMaskModule } from 'primeng/inputmask';
import { CreateOrderComponent } from '../create-order/create-order.component';
import { Customer } from '../../../models/customer.model';
import { Dependent } from '../../../models/dependent.model';
import { CustomerOrder } from '../../../models/customer-order.model';
import { StateService } from '../../../services/state.service';
import { CityService } from '../../../services/city.service';
import { State } from '../../../models/state.model';
import { City } from '../../../models/city.model';
import { TextareaModule } from 'primeng/textarea';
import { CommonModule, Location } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-customer-form',
  imports: [
    CommonModule,
    TabsModule,
    InputNumberModule,
    DatePickerModule,
    InputMaskModule,
    InputTextModule,
    ReactiveFormsModule,
    SelectModule,
    RadioButtonModule,
    CheckboxModule,
    CreateOrderComponent,
    TextareaModule,
    ToastModule, 
],
  providers: [MessageService],
  templateUrl: './customer-form.component.html'
})
export class CustomerFormComponent implements OnInit {

  id!: number;
  form!: FormGroup;
  isEditMode: boolean = false;
  states: State[] = [];
  cities: City[] = [];
  customerOrder!: CustomerOrder;
  activeTab: string = '0';

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private customerService: CustomerService,
    private stateService: StateService,
    private cityService: CityService,
    private router: Router,
    private messageService: MessageService,
    private location: Location
  ) {}

  /** Cadastro aberto a partir do PDV (nova aba): devolve cliente ao PDV via localStorage. */
  private fromPdv = false;

  ngOnInit(): void {
    this.fromPdv = this.activatedRoute.snapshot.queryParamMap.get('fromPdv') === '1';

    this.form = this.fb.group({
        active: [true],
        fullName: ['', Validators.required],
        nickName: [''],
        contact: [''],
        observations: [''],
        cpf: ['', Validators.required],
        rg: [''],
        birthDay: [],
        creditLimit: [0],
        cashBack: [null],
        cep: [''],
        address: [''],
        addressNumber: [''],
        addressComplement: [''],
        district: [''],
        state: [''],
        city: [''],
        phone: [''],
        homePhone: [''],
        whatsApp: [''],
        email: [''],
        situation: [''],
        dueDay: [],
        usePriceTable: [false],
        selectedPrice: [null],
        dependents: this.fb.array([])
    })

    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    if(idParam){
      this.id = parseInt(idParam);
    }

    if (this.id) {
      this.isEditMode = true;
      
      this.customerService.getById(this.id).subscribe({
        next: (customer) => {
        
        if(customer.data.state){
          this.getCities({value: customer.data.state});
        }

        const customerData = {...customer.data};
        if (customerData.birthDay && typeof customerData.birthDay === 'string') {
          customerData.birthDay = new Date(customerData.birthDay);
        }

        this.form.patchValue({...customerData, selectedPrice: 
          customer.data.moneyPriceTable ? 'money' : customer.data.installmentPriceTable  ? 'installment' : customer.data.wholeSalePriceTable ? 'wholesale': null
        });

        if (customer.data.dependents?.length) {
          const dependentsArray = this.form.get('dependents') as FormArray;
          dependentsArray.clear();
          customer.data.dependents.forEach((d: Dependent) => dependentsArray.push(this.createDependentGroup(d)));
        }
      },
      error: (error) => {
        console.log(error);
      }
    })
    }

    this.getStates();

    this.form.get('usePriceTable')?.valueChanges.subscribe(use => {
      const selected = this.form.get('selectedPrice');
      if (use){
        selected?.enable();
      } 
      else {
        selected?.disable();
        this.form.patchValue({selectedPrice: null})
      }
    });

  }

  getStates(){
    this.stateService.getAll('', false, null).subscribe({
      next: (result) => {
        this.states = result.data;
      }
    });
  }

  getCities(event: any){
    this.cityService.getAll('', null, event.value, false, null).subscribe({
      next: (data) => {
        this.cities = data.data
      }
    });
  }

  get dependentsArray(): FormArray {
    return this.form?.get('dependents') as FormArray;
  }

  createDependentGroup(dependent?: Partial<Dependent>): FormGroup {
    return this.fb.group({
      id: [dependent?.id ?? null],
      fullName: [dependent?.fullName ?? '', Validators.required],
      cpf: [dependent?.cpf ?? '', Validators.required],
      phone: [dependent?.phone ?? '']
    });
  }

  addDependent = (): void => {
    this.dependentsArray.push(this.createDependentGroup());
  };

  removeDependent = (index: number): void => {
    this.dependentsArray.removeAt(index);
  };

  goBack(){
    this.location.back();
  }

  onSaleSelected(order: CustomerOrder)
  {
    this.customerOrder = order;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    const raw = this.form.getRawValue();
    const dependents: Dependent[] = (raw.dependents || []).map((d: { id?: number; fullName: string; cpf: string; phone: string }) => ({
      id: d.id,
      fullName: d.fullName,
      cpf: d.cpf,
      phone: d.phone
    }));
    var customer: Customer = {...this.form.value,
      order: this.customerOrder,
      moneyPriceTable: raw.selectedPrice === 'money',
      installmentPriceTable: raw.selectedPrice === 'installment',
      wholeSalePriceTable: raw.selectedPrice === 'wholesale',
      dependents
    };

    console.log(customer);

    if(this.isEditMode){
        this.customerService.update(this.id, customer).subscribe({
          next: () => this.goBack(),
          error: error => this.showErrorMessage(error)
        });
    }else{
      this.customerService.create(customer).subscribe({
      next: (created) => {
        if (this.fromPdv && created?.id) {
          try {
            localStorage.setItem('pdvNewCustomerV1', JSON.stringify(created));
          } catch {
            /* ignore */
          }
          if (window.opener) {
            window.close();
            return;
          }
          void this.router.navigateByUrl('/pdv');
          return;
        }
        this.goBack();
      },
      error: error => {
        this.showErrorMessage(error);
      }
    });
    }
  }

  showErrorMessage(error: any) {
    let msg = 'Erro!';
      if(error.error){
          msg = error.error.message;
          error.error.details.forEach((d: any) => {
          this.messageService.add({ severity: 'error', summary: msg, detail: d, life: 4000 });
        });
      }
  }
  
}